// components/ui/Select.jsx - Shadcn style Select
import React, { useState, createContext, useContext, useRef, useEffect } from "react";
import { ChevronDown, Check, Search, X } from "lucide-react";
import { cn } from "../../utils/cn";
import Button from "./Button";
import Input from "./Input";

// Create Select context for sharing state between components
const SelectContext = createContext();

// Main Select component
const Select = ({ 
  label, 
  value, 
  onValueChange, 
  options = [], 
  placeholder = "Select...", 
  disabled = false, 
  readOnly = false,
  searchable = false,
  className = "",
  error,
  helperText,
  required = false,
  onChange,
  name,
  multiple = false,
  loading = false,
  clearable = false,
  onOpenChange,
  children,
  description,
  selectId,
  ref,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const selectRef = useRef(null);

  // CRITICAL FIX: Treat readOnly same as disabled
  const isSelectDisabled = disabled || readOnly;

  // FIXED: Enhanced click outside handling for dropdown closure
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef?.current && !selectRef?.current?.contains(event?.target)) {
        setIsOpen(false);
        onOpenChange?.(false);
        setSearchTerm("");
      }
    };

    const handleEscapeKey = (event) => {
      if (event?.key === 'Escape' && isOpen) {
        setIsOpen(false);
        onOpenChange?.(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onOpenChange]);

  // FIXED: Enhanced value change handler with better backward compatibility
  const handleValueChange = (newValue) => {
    // For onChange, create synthetic event for backward compatibility
    if (onChange) {
      const syntheticEvent = {
        target: { value: newValue, name: name },
        currentTarget: { value: newValue, name: name },
        preventDefault: () => {},
        stopPropagation: () => {}
      };
      onChange(syntheticEvent);
    }
    
    // For onValueChange, pass value directly
    if (onValueChange) {
      onValueChange(newValue);
    }
  };

  // Filter options based on search
  const filteredOptions = searchable && searchTerm
      ? options?.filter(option =>
          option?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          (option?.value && option?.value?.toString()?.toLowerCase()?.includes(searchTerm?.toLowerCase()))
      )
      : options;

  // FIXED: Enhanced display logic to properly show selected values
  const getSelectedDisplay = () => {
      if (!value && value !== 0) return placeholder;

      if (multiple) {
          const selectedOptions = options?.filter(opt => value?.includes(opt?.value));
          if (selectedOptions?.length === 0) return placeholder;
          if (selectedOptions?.length === 1) return selectedOptions?.[0]?.label;
          return `${selectedOptions?.length} items selected`;
      }

      const selectedOption = options?.find(opt => opt?.value === value);
      return selectedOption?.label || value || placeholder;
  };

  const handleToggle = (e) => {
      e?.preventDefault();
      e?.stopPropagation();
      if (!disabled) {
          const newIsOpen = !isOpen;
          setIsOpen(newIsOpen);
          onOpenChange?.(newIsOpen);
          if (!newIsOpen) {
              setSearchTerm("");
          }
      }
  };

  // FIXED: Simplified option selection with immediate closure for single select
  const handleOptionSelect = (option, e) => {
      if (e) {
          e?.preventDefault();
          e?.stopPropagation();
      }
      
      if (multiple) {
          const currentValue = value || [];
          const updatedValue = currentValue?.includes(option?.value)
              ? currentValue?.filter(v => v !== option?.value)
              : [...currentValue, option?.value];
          handleValueChange(updatedValue);
      } else {
          handleValueChange(option?.value);
          // Close dropdown immediately for single select
          setIsOpen(false);
          onOpenChange?.(false);
          setSearchTerm("");
      }
  };

  const handleClear = (e) => {
      e?.stopPropagation();
      handleValueChange(multiple ? [] : '');
  };

  const handleSearchChange = (e) => {
      setSearchTerm(e?.target?.value);
  };

  const isSelected = (optionValue) => {
      if (multiple) {
          return value?.includes(optionValue) || false;
      }
      return value === optionValue;
  };

  const hasValue = multiple ? value?.length > 0 : value !== undefined && value !== '';

  // FIXED: Enhanced context value with proper methods and options
  const contextValue = {
      value,
      isOpen,
      disabled,
      placeholder,
      options, // CRITICAL: Pass options to context for SelectValue
      onToggle: handleToggle,
      onValueChange: handleValueChange,
      onClose: () => {
          setIsOpen(false);
          onOpenChange?.(false);
          setSearchTerm("");
      },
      isSelected,
      hasValue,
      loading,
      multiple
  };

  // If children are provided, use composition pattern
  if (children) {
      return (
          <SelectContext.Provider value={contextValue}>
              <div ref={selectRef} className={cn("relative", className)}>
                  {label && (
                      <label
                          htmlFor={selectId}
                          className={cn(
                              "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mb-2 block",
                              error ? "text-destructive" : "text-foreground"
                          )}
                      >
                          {label}
                          {required && <span className="text-destructive ml-1">*</span>}
                      </label>
                  )}
                  {children}
                  {description && !error && (
                      <p className="text-sm text-muted-foreground mt-1">
                          {description}
                      </p>
                  )}
                  {error && (
                      <p className="text-sm text-destructive mt-1">
                          {error}
                      </p>
                  )}
              </div>
          </SelectContext.Provider>
      );
  }

  // Legacy pattern with options prop
  return (
      <div ref={selectRef} className={cn("relative", className)}>
          {label && (
              <label
                  htmlFor={selectId}
                  className={cn(
                      "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mb-2 block",
                      error ? "text-destructive" : "text-foreground"
                  )}
              >
                  {label}
                  {required && <span className="text-destructive ml-1">*</span>}
              </label>
          )}
          <div className="relative">
              <button
                  ref={ref}
                  id={selectId}
                  type="button"
                  className={cn(
                      "flex h-10 w-full items-center justify-between rounded-md border border-input bg-white text-black px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                      error && "border-destructive focus:ring-destructive",
                      !hasValue && "text-muted-foreground"
                  )}
                  onClick={handleToggle}
                  disabled={disabled}
                  aria-expanded={isOpen}
                  aria-haspopup="listbox"
                  {...props}
              >
                  <span className="truncate">{getSelectedDisplay()}</span>

                  <div className="flex items-center gap-1">
                      {loading && (
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                      )}

                      {clearable && hasValue && !loading && (
                          <Button
                              variant="ghost"
                              size="icon"
                              className="h-4 w-4"
                              onClick={handleClear}
                          >
                              <X className="h-3 w-3" />
                          </Button>
                      )}

                      <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
                  </div>
              </button>

              {/* Hidden native select for form submission */}
              <select
                  name={name}
                  value={value || ''}
                  onChange={() => { }} // Controlled by our custom logic
                  className="sr-only"
                  tabIndex={-1}
                  multiple={multiple}
                  required={required}
              >
                  <option value="">Select...</option>
                  {options?.map(option => (
                      <option key={option?.value} value={option?.value}>
                          {option?.label}
                      </option>
                  ))}
              </select>

              {/* Dropdown with proper event handling */}
              {isOpen && (
                  <div 
                      className="absolute z-[9999] w-full mt-1 bg-white text-black border border-border rounded-md shadow-lg max-h-60 overflow-auto"
                      onClick={(e) => e?.stopPropagation()}
                  >
                      {searchable && (
                          <div className="p-2 border-b sticky top-0 bg-white">
                              <div className="relative">
                                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                                  <Input
                                      placeholder="Search options..."
                                      value={searchTerm}
                                      onChange={handleSearchChange}
                                      className="pl-8"
                                  />
                              </div>
                          </div>
                      )}

                      <div className="py-1">
                          {filteredOptions?.length === 0 ? (
                              <div className="px-3 py-2 text-sm text-muted-foreground">
                                  {searchTerm ? 'No options found' : 'No options available'}
                              </div>
                          ) : (
                              filteredOptions?.map((option) => (
                                  <div
                                      key={option?.value}
                                      className={cn(
                                          "relative flex cursor-pointer select-none items-center rounded-sm px-3 py-2 text-sm outline-none transition-colors",
                                          isSelected(option?.value) 
                                              ? "bg-primary text-primary-foreground hover:bg-primary/90" 
                                              : "hover:bg-accent hover:text-accent-foreground",
                                          option?.disabled && "pointer-events-none opacity-50"
                                      )}
                                      onClick={(e) => !option?.disabled && handleOptionSelect(option, e)}
                                      onMouseDown={(e) => e?.preventDefault()}
                                  >
                                      <span className="flex-1">{option?.label}</span>
                                      {multiple && isSelected(option?.value) && (
                                          <Check className="h-4 w-4 ml-2" />
                                      )}
                                      {option?.description && (
                                          <span className="text-xs text-muted-foreground ml-2">
                                              {option?.description}
                                          </span>
                                      )}
                                  </div>
                              ))
                          )}
                      </div>
                  </div>
              )}
          </div>
          {description && !error && (
              <p className="text-sm text-muted-foreground mt-1">
                  {description}
              </p>
          )}
          {error && (
              <p className="text-sm text-destructive mt-1">
                  {error}
              </p>
          )}
      </div>
  );
};

Select.displayName = "Select";

// SelectTrigger Component
const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => {
    const context = useContext(SelectContext);
    
    return (
        <button
            ref={ref}
            type="button"
            className={cn(
                "flex h-10 w-full items-center justify-between rounded-md border border-input bg-white text-black px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                !context?.hasValue && "text-muted-foreground",
                className
            )}
            onClick={context?.onToggle}
            disabled={context?.disabled}
            aria-expanded={context?.isOpen}
            aria-haspopup="listbox"
            {...props}
        >
            {children}
            <div className="flex items-center gap-1">
                {context?.loading && (
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                )}
                <ChevronDown className={cn("h-4 w-4 transition-transform", context?.isOpen && "rotate-180")} />
            </div>
        </button>
    );
});

SelectTrigger.displayName = "SelectTrigger";

// FIXED: SelectValue with proper label resolution from options
const SelectValue = ({ placeholder, ...props }) => {
    const context = useContext(SelectContext);
    
    if (!context?.value && context?.value !== 0) {
        return (
            <span className="text-muted-foreground">
                {placeholder || context?.placeholder || "Select an option"}
            </span>
        );
    }
    
    // FIXED: Properly resolve display value from options
    if (context?.multiple) {
        const count = Array.isArray(context?.value) ? context?.value?.length : 0;
        return <span className="truncate">{count > 0 ? `${count} items selected` : placeholder || "Select options"}</span>;
    }
    
    // For single select, find the label from options
    const selectedOption = context?.options?.find(opt => opt?.value === context?.value);
    const displayValue = selectedOption?.label || context?.value || placeholder || context?.placeholder;
    
    return <span className="truncate">{displayValue}</span>;
};

SelectValue.displayName = "SelectValue";

// SelectContent Component
const SelectContent = ({ className, children, ...props }) => {
    const context = useContext(SelectContext);
    
    if (!context?.isOpen) return null;
    
    return (
        <div 
            className={cn(
                "absolute z-50 w-full mt-1 bg-white text-black border border-border rounded-md shadow-md",
                className
            )}
            {...props}
        >
            <div className="py-1 max-h-60 overflow-auto">
                {children}
            </div>
        </div>
    );
};

SelectContent.displayName = "SelectContent";

// FIXED: SelectItem with proper click handling and closure
const SelectItem = React.forwardRef(({ className, children, value, disabled, ...props }, ref) => {
    const context = useContext(SelectContext);
    
    const handleClick = (e) => {
        if (e) {
            e?.preventDefault();
            e?.stopPropagation();
        }
        
        if (!disabled && context?.onValueChange) {
            context?.onValueChange(value);
            
            // Close dropdown for single select
            if (!context?.multiple) {
                context?.onClose?.();
            }
        }
    };
    
    const isSelected = context?.isSelected?.(value);
    
    return (
        <div
            ref={ref}
            className={cn(
                "relative flex cursor-pointer select-none items-center rounded-sm px-3 py-2 text-sm outline-none transition-colors",
                isSelected 
                    ? "bg-primary text-primary-foreground hover:bg-primary/90" 
                    : "hover:bg-accent hover:text-accent-foreground",
                disabled && "pointer-events-none opacity-50",
                className
            )}
            onClick={handleClick}
            onMouseDown={(e) => e?.preventDefault()}
            {...props}
        >
            <span className="flex-1">{children}</span>
            {(context?.multiple && isSelected) && (
                <Check className="h-4 w-4 ml-2" />
            )}
        </div>
    );
});

SelectItem.displayName = "SelectItem";

// Export all components
export default Select;
export { SelectTrigger, SelectValue, SelectContent, SelectItem };